/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Alert, Box, Select, Stack, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { DAY_KEYS } from '../../../resources/appointment/operations';
import type { DayKey, InternalDayState } from '../../../resources/appointment/operations';

import { AppointmentDayRow } from '../components/AppointmentDayRow';

const TIMEZONES = [
    'Europe/Paris',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Rome',
    'Europe/Berlin',
    'Europe/Amsterdam',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Australia/Sydney',
];

type Props = {
    days: Record<DayKey, InternalDayState>;
    timezone: string;
    hasNoSlots: boolean;
    onToggleDay: (dayKey: DayKey, active: boolean) => void;
    onSlotStartChange: (dayKey: DayKey, slotIdx: number, newStart: string) => void;
    onSlotEndChange: (dayKey: DayKey, slotIdx: number, newEnd: string) => void;
    onDeleteSlot: (dayKey: DayKey, slotIdx: number) => void;
    onAddSlot: (dayKey: DayKey) => void;
    onTimezoneChange: (timezone: string) => void;
};

export const AppointmentSlotsPartial = ({
    days,
    timezone,
    hasNoSlots,
    onToggleDay,
    onSlotStartChange,
    onSlotEndChange,
    onDeleteSlot,
    onAddSlot,
    onTimezoneChange,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    return (
        <Stack data-spec-id="agenda-section-slots" spacing={3}>
            <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, fontSize: 20, mb: 0.5 }}>
                    {formatMessage({ id: 'appointment.slots.title' })}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {formatMessage({ id: 'appointment.slots.description' })}
                </Typography>
            </Box>

            {hasNoSlots && (
                <Alert severity="error" data-spec-id="no-slots-error">
                    {formatMessage({ id: 'appointment.slots.no_slots_error' })}
                </Alert>
            )}

            <Stack spacing={0}>
                {DAY_KEYS.map((dayKey) => (
                    <AppointmentDayRow
                        key={dayKey}
                        dayKey={dayKey}
                        label={formatMessage({ id: `appointment.day.long.${dayKey}` })}
                        active={days[dayKey].active}
                        periods={days[dayKey].periods}
                        onToggle={(active) => onToggleDay(dayKey, active)}
                        onSlotStartChange={(slotIdx, val) =>
                            onSlotStartChange(dayKey, slotIdx, val)
                        }
                        onSlotEndChange={(slotIdx, val) => onSlotEndChange(dayKey, slotIdx, val)}
                        onDeleteSlot={(slotIdx) => onDeleteSlot(dayKey, slotIdx)}
                        onAddSlot={() => onAddSlot(dayKey)}
                    />
                ))}
            </Stack>

            {/* Fuseau horaire */}
            <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatMessage({ id: 'appointment.timezone.label' })}
                </Typography>
                <Select
                    native
                    value={timezone}
                    onChange={(e) => onTimezoneChange(e.target.value)}
                    size="small"
                    sx={{ minWidth: 280 }}
                    inputProps={{ 'data-spec-id': 'agenda-timezone-select' } as object}
                >
                    {(TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]).map(
                        (tz) => (
                            <option key={tz} value={tz}>
                                {tz.replace(/_/g, ' ')}
                            </option>
                        ),
                    )}
                </Select>
            </Box>
        </Stack>
    );
};
